import { LeadTimeIndicator } from '@ravago/shared/page-data/models/elements/product-lead-time.model';

export interface LeadTimeCheckCriteria {
   colorId: number;
   producerColorCode?: string;
   packagingGroupId?: number;
   addressId?: number;
   countryCode?: string;
}

export interface LeadTimeFilterCriteria {
   packagingGroups: Array<{ id: number }>;
   addresses?: Array<{ id: number }>;
   countries?: Array<{ code: string }>;
}

const normalizeCountryCode = (countryCode: string): string => (countryCode === 'GB' ? 'UK' : countryCode.toUpperCase());

const matchesColor = (indicator: LeadTimeIndicator, criteria: LeadTimeCheckCriteria): boolean => {
   if (indicator.color.producerColorCode !== undefined || criteria.producerColorCode !== undefined) {
      return indicator.color.id === criteria.colorId && indicator.color.producerColorCode === criteria.producerColorCode;
   }
   return indicator.color.id === criteria.colorId;
};

const matchesPackaging = (indicator: LeadTimeIndicator, criteria: LeadTimeCheckCriteria): boolean => {
   if (criteria.packagingGroupId === undefined) {
      return true;
   }
   return indicator.packagingGroupId === criteria.packagingGroupId;
};

const matchesAddressOrCountry = (indicator: LeadTimeIndicator, criteria: LeadTimeCheckCriteria, isGuestOrPending: boolean): boolean => {
   if (isGuestOrPending) {
      if (criteria.countryCode === undefined) {
         return true;
      }
      const normalizedCriteriaCountryCode = normalizeCountryCode(criteria.countryCode);
      return indicator.countryCode === normalizedCriteriaCountryCode;
   }

   if (criteria.addressId === undefined) {
      return true;
   }
   return indicator.addressId === criteria.addressId;
};

const hasMtsLocalStockingPolicy = (indicators: LeadTimeIndicator[]): boolean =>
   indicators.some((indicator) => indicator.stockingPolicy === 'MTS LOCAL');

const filterByCriteria = (indicators: LeadTimeIndicator[], criteria: LeadTimeCheckCriteria, isGuestOrPending: boolean): LeadTimeIndicator[] =>
   indicators.filter(
      (indicator) =>
         matchesColor(indicator, criteria) && matchesPackaging(indicator, criteria) && matchesAddressOrCountry(indicator, criteria, isGuestOrPending)
   );

/**
 * Checks if there is MTS LOCAL availability for the given criteria
 * @param indicators - Array of lead time indicators
 * @param criteria - The criteria to check against
 * @param isGuestOrPending - Whether the user is guest (true = country-based, false = address-based)
 * @returns true if MTS LOCAL is available, false otherwise
 */
export const hasMtsLocalLeadTime = (indicators: LeadTimeIndicator[], criteria: LeadTimeCheckCriteria, isGuestOrPending = false): boolean => {
   if (indicators.length === 0) {
      return false;
   }

   const filteredIndicators = filterByCriteria(indicators, criteria, isGuestOrPending);
   return hasMtsLocalStockingPolicy(filteredIndicators);
};

/**
 * Filters lead time indicators based on available packaging groups and addresses/countries
 * @param indicators - Array of lead time indicators
 * @param filterCriteria - The filter criteria containing packaging groups and addresses or countries
 * @returns Filtered array of lead time indicators
 */
export const filterLeadTimeIndicators = (indicators: LeadTimeIndicator[], filterCriteria: LeadTimeFilterCriteria): LeadTimeIndicator[] => {
   if (indicators.length === 0) {
      return [];
   }

   return indicators.filter((indicator) => {
      const packagingGroupMatch = filterCriteria.packagingGroups.some((group) => group.id === indicator.packagingGroupId);
      if (!packagingGroupMatch) {
         return false;
      }

      if (filterCriteria.addresses && filterCriteria.addresses.length > 0) {
         return filterCriteria.addresses.some((address) => address.id === indicator.addressId);
      }

      if (filterCriteria.countries && filterCriteria.countries.length > 0) {
         return filterCriteria.countries.some((country) => normalizeCountryCode(country.code) === indicator.countryCode);
      }

      return true;
   });
};
